import {
  Badge,
  Button,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Copy24Regular,
  Delete24Regular,
  Edit24Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import { useRef, useState } from 'react';

import type { ConfigRailProps, RenameState } from './ConfigRail.interface.js';
import { useStyles } from './ConfigRail.style.js';

function methodColor(
  method: string,
): 'success' | 'warning' | 'danger' | 'informative' | 'subtle' {
  switch (method) {
    case 'GET':
      return 'success';
    case 'POST':
      return 'informative';
    case 'PUT':
      return 'warning';
    case 'PATCH':
      return 'warning';
    case 'DELETE':
      return 'danger';
    default:
      return 'subtle';
  }
}

export function ConfigRail({
  savedConfigs,
  activeTabId,
  onOpenConfig,
  onNewConfig,
  onDeleteConfig,
  onRenameConfig,
  onCloneConfig,
}: ConfigRailProps) {
  const styles = useStyles();
  const [renaming, setRenaming] = useState<RenameState | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function startRename(configId: string, currentName: string) {
    setRenaming({ id: configId, value: currentName });
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function commitRename() {
    if (!renaming) return;
    const trimmed = renaming.value.trim();
    if (trimmed) onRenameConfig(renaming.id, trimmed);
    setRenaming(null);
  }

  function handleRenameKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commitRename();
    if (e.key === 'Escape') setRenaming(null);
  }

  return (
    <div className={styles.rail}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>Saved Requests</span>
        <Button
          appearance="subtle"
          icon={<Add24Regular />}
          size="small"
          onClick={onNewConfig}
          title="New request"
        />
      </div>

      {savedConfigs.length === 0 ? (
        <Text className={styles.emptyState}>No saved requests yet</Text>
      ) : (
        savedConfigs.map((config) => {
          const isActive = activeTabId === config.id;
          const isRenaming = renaming?.id === config.id;
          const showMenu =
            !isRenaming &&
            (hoveredId === config.id || openMenuId === config.id);

          return (
            <div
              key={config.id}
              role="button"
              tabIndex={0}
              className={`${styles.configItem} ${isActive ? styles.configItemActive : ''}`}
              onClick={() => !isRenaming && onOpenConfig(config.id)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isRenaming)
                  onOpenConfig(config.id);
              }}
              onMouseEnter={() => setHoveredId(config.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className={styles.configInfo}>
                <div className={styles.configLabel}>
                  <Badge
                    appearance="tint"
                    color={methodColor(config.method)}
                    size="small"
                    style={{
                      flexShrink: 0,
                      fontFamily: 'monospace',
                      fontSize: '10px',
                    }}
                  >
                    {config.method}
                  </Badge>

                  {isRenaming ? (
                    <Input
                      ref={inputRef}
                      className={styles.renameInput}
                      size="small"
                      value={renaming.value}
                      onChange={(_, d) =>
                        setRenaming({ id: config.id, value: d.value })
                      }
                      onBlur={commitRename}
                      onKeyDown={handleRenameKey}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <Text className={styles.configName}>{config.name}</Text>
                  )}
                </div>
                {config.url && !isRenaming && (
                  <Text className={styles.configUrl}>{config.url}</Text>
                )}
              </div>

              {showMenu && (
                <Menu
                  onOpenChange={(_, data) =>
                    setOpenMenuId(data.open ? config.id : null)
                  }
                >
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      appearance="subtle"
                      icon={<MoreHorizontal20Regular />}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem
                        icon={<Edit24Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          startRename(config.id, config.name);
                        }}
                      >
                        Rename
                      </MenuItem>
                      <MenuItem
                        icon={<Copy24Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCloneConfig(config.id);
                        }}
                      >
                        Clone
                      </MenuItem>
                      <MenuItem
                        icon={<Delete24Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteConfig(config.id);
                        }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
